/**
 * PackageCard component.
 *
 * Renders specialist/EHS recommendation cards. Supports two layouts:
 * - Full-width (single card): one card takes the full width
 * - Side-by-side (2 cards): two cards in a comparison layout
 *
 * Each card displays: name, key details, price, and a CTA button.
 * Entity-branded button colors via CSS custom properties.
 *
 * REQ-D-037 (Section 8.2.3): when the bot recommends ONE specific
 * package the layout is full-width AND the card carries a
 * "RECOMMENDED" badge in the top-right corner. The badge is rendered
 * iff (cards.length === 1 AND cards[0].recommended === true) OR
 * (cards.length === 1 AND no card sets recommended explicitly — the
 * single-card layout is itself the bot's recommendation per spec).
 */

import type { ChatMessage, CardData } from "../../types";
import { EXTERNAL_LINK_SVG } from "../icons";

export interface PackageCardCallbacks {
  onAction: (cardId: string) => void;
  onMessage?: (message: string) => void;
}

export interface PackageCardOptions {
  message: ChatMessage;
  onAction: (cardId: string) => void;
  callbacks?: PackageCardCallbacks;
}

export class PackageCard {
  private readonly element: HTMLElement;

  constructor(options: PackageCardOptions) {
    const { message, onAction, callbacks } = options;
    const cards = message.cards ?? [];

    this.element = document.createElement("div");
    this.element.className = "hana-cards";
    this.element.setAttribute("data-message-id", message.id);

    // Optional header text above cards
    if (message.content) {
      const header = document.createElement("div");
      header.className = "hana-cards__header";
      header.textContent = message.content;
      this.element.appendChild(header);
    }

    // Card container: single (full-width) or comparison (2-per-row)
    const container = document.createElement("div");
    container.className =
      cards.length === 1
        ? "hana-cards__grid hana-cards__grid--single"
        : "hana-cards__grid hana-cards__grid--comparison";

    // REQ-D-037: a single-card message is by definition the bot's
    // recommendation (Section 8.2.3 — "full-width card: used when a
    // package is explicitly recommended by the bot"). Surface the
    // RECOMMENDED badge unless the card explicitly sets
    // recommended === false (escape hatch for callers that want a
    // single full-width card without the badge — e.g. a lone
    // package shown as the only available option, not a pick).
    const isSingleRecommended =
      cards.length === 1 && cards[0]!.recommended !== false;

    cards.forEach((card: CardData) => {
      container.appendChild(
        this.createCard(card, onAction, isSingleRecommended),
      );
    });

    this.element.appendChild(container);

    // "Help me choose" button when multiple cards are shown
    if (cards.length > 1) {
      const helpBtn = document.createElement("button");
      helpBtn.className = "hana-cards__help-choose";
      helpBtn.setAttribute("type", "button");
      helpBtn.textContent = "Help me choose the right package";
      helpBtn.addEventListener("click", () => {
        const sendMessage = callbacks?.onMessage ?? onAction;
        sendMessage("Help me choose the right package");
      });
      this.element.appendChild(helpBtn);
    }
  }

  getElement(): HTMLElement {
    return this.element;
  }

  private createCard(
    card: CardData,
    onAction: (cardId: string) => void,
    showRecommendedBadge: boolean = false,
  ): HTMLElement {
    const cardEl = document.createElement("div");
    cardEl.className = "hana-card";
    cardEl.setAttribute("data-card-id", card.id);
    if (showRecommendedBadge) {
      cardEl.classList.add("hana-card--recommended");
    }

    // RECOMMENDED badge (REQ-D-037) — top-right corner; relies on
    // .hana-card position:relative + .hana-card__badge absolute.
    if (showRecommendedBadge) {
      const badgeEl = document.createElement("span");
      badgeEl.className = "hana-card__badge";
      badgeEl.setAttribute("role", "status");
      badgeEl.setAttribute("aria-label", "Recommended package");
      badgeEl.textContent = "RECOMMENDED";
      cardEl.appendChild(badgeEl);
    }

    // Name
    const nameEl = document.createElement("div");
    nameEl.className = "hana-card__name";
    nameEl.textContent = card.name;
    cardEl.appendChild(nameEl);

    // Details
    if (card.details.length > 0) {
      const detailsEl = document.createElement("ul");
      detailsEl.className = "hana-card__details";
      card.details.forEach((detail: string) => {
        const li = document.createElement("li");
        li.className = "hana-card__detail";
        li.textContent = detail;
        detailsEl.appendChild(li);
      });
      cardEl.appendChild(detailsEl);
    }

    // Price
    if (card.price) {
      const priceEl = document.createElement("div");
      priceEl.className = "hana-card__price";
      priceEl.textContent = card.price;
      cardEl.appendChild(priceEl);
    }

    // CTA button with external link icon
    const ctaBtn = document.createElement("button");
    ctaBtn.className = "hana-card__cta";
    ctaBtn.setAttribute("type", "button");

    const ctaLabel = document.createElement("span");
    ctaLabel.textContent = card.ctaText;
    ctaBtn.appendChild(ctaLabel);

    const ctaIcon = document.createElement("span");
    ctaIcon.className = "hana-card__cta-icon";
    ctaIcon.innerHTML = EXTERNAL_LINK_SVG;
    ctaBtn.appendChild(ctaIcon);

    ctaBtn.addEventListener("click", () => {
      onAction(card.id);
    });
    cardEl.appendChild(ctaBtn);

    return cardEl;
  }
}
